/**
 * Wave pick list — SKU-grouped walk sheet for one Printing Toolkit / Packers
 * folder selection. Complements per-order packing slips (competitors print
 * this; we also attach it to the mixed physical stack).
 */
import {
  getPackingSlipItemSku,
  getPackingSlipItems,
} from './packingSlipModel';
import {
  getPrintingOrderCompany,
  getPrintingOrderDeliveryDay,
  getPrintingOrderShippingMethod,
} from './printingToolkitFilterOptions';

import { getQueueOrderKey } from '../services/printQueueModel';

/**
 * Wave / matrix / attach order key — same contract as the mixed print queue.
 * Prefer OrderDesk source_id; never prefer display orderNumber over source.
 */
export const getWaveOrderKey = (order) => getQueueOrderKey(order);

const itemName = (item) =>
  String(item?.name || item?.product_name || item?.productName || '').trim();

const itemQuantity = (item) => {
  const parsed = Number(item?.quantity ?? item?.qty ?? item?.amount);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const itemLot = (item) =>
  String(
    item?.lot ||
      item?.lotNumber ||
      item?.lot_number ||
      item?.batch ||
      item?.batch_number ||
      item?.batchNumber ||
      item?.metadata?.lot ||
      '',
  ).trim();

const itemUseBy = (item) =>
  String(
    item?.use_by ||
      item?.useBy ||
      item?.best_before ||
      item?.bestBefore ||
      item?.expiry ||
      item?.expiry_date ||
      '',
  ).trim();

/**
 * @param {object[]} orders
 * @param {{
 *   folderLabel?: string,
 *   formatName?: (sku: string, fallback: string) => string,
 *   formatPack?: (sku: string) => string,
 *   isPacked?: (order: object) => boolean,
 *   isShipped?: (order: object) => boolean,
 * }} [options]
 */
export function buildWavePickList(orders, options = {}) {
  const list = Array.isArray(orders) ? orders.filter(Boolean) : [];
  const formatName = options.formatName || ((sku, fallback) => fallback || sku);
  const formatPack = options.formatPack || (() => '');
  const isPacked = options.isPacked || (() => false);
  const isShipped = options.isShipped || (() => false);
  const skuMap = new Map();
  const shippingCounts = new Map();
  const deliveryDays = new Set();

  list.forEach((order) => {
    const orderKey = getWaveOrderKey(order);
    const company = getPrintingOrderCompany(order);
    const shippingMethod = getPrintingOrderShippingMethod(order) || 'Unspecified';
    shippingCounts.set(shippingMethod, (shippingCounts.get(shippingMethod) || 0) + 1);
    const deliveryDay = getPrintingOrderDeliveryDay(order);
    if (deliveryDay) deliveryDays.add(deliveryDay);
    const packed = isPacked(order);
    const shipped = isShipped(order);

    getPackingSlipItems(order).forEach((item) => {
      const sku = getPackingSlipItemSku(item);
      const quantity = itemQuantity(item);
      if (!sku || quantity <= 0) return;
      const current = skuMap.get(sku) || {
        sku,
        name: formatName(sku, itemName(item)),
        packSize: formatPack(sku),
        quantity: 0,
        lots: new Set(),
        useBy: new Set(),
        orders: [],
      };
      if (!current.name) current.name = formatName(sku, itemName(item));
      current.quantity += quantity;
      const lot = itemLot(item);
      if (lot) current.lots.add(lot);
      const useBy = itemUseBy(item);
      if (useBy) current.useBy.add(useBy);
      current.orders.push({
        orderKey,
        company,
        shippingMethod,
        quantity,
        packed,
        shipped,
        lot,
        useBy,
      });
      skuMap.set(sku, current);
    });
  });

  const lines = Array.from(skuMap.values())
    .map((line) => ({
      sku: line.sku,
      name: line.name,
      packSize: line.packSize,
      quantity: line.quantity,
      orderCount: new Set(line.orders.map((entry) => entry.orderKey)).size,
      lots: Array.from(line.lots),
      useBy: Array.from(line.useBy),
      orders: line.orders,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB') || a.sku.localeCompare(b.sku));

  return {
    folderLabel: String(options.folderLabel || '').trim(),
    generatedAt: options.generatedAt || new Date().toISOString(),
    orderCount: list.length,
    skuCount: lines.length,
    unitCount: lines.reduce((sum, line) => sum + line.quantity, 0),
    deliveryDays: Array.from(deliveryDays).sort(),
    shippingMethods: Array.from(shippingCounts.entries())
      .map(([method, orderCount]) => ({ method, orderCount }))
      .sort((a, b) => b.orderCount - a.orderCount || a.method.localeCompare(b.method)),
    lines,
  };
}

export function describeWaveStack(wave, recipe = {}) {
  const format =
    recipe.format === 'cheetah'
      ? 'cheetah'
      : recipe.format === 'go4'
        ? 'go4'
        : recipe.format === 'auto'
          ? 'auto'
          : 'standard';
  const steps = [];
  if (recipe.includePickList !== false) {
    steps.push({
      key: 'pick-list',
      label: 'Wave pick list',
      detail: 'Walk the cold room once. Tick SKUs, then pack to the order slips.',
    });
  }
  steps.push({
    key:
      format === 'cheetah'
        ? 'cheetah'
        : format === 'go4'
          ? 'go4'
          : format === 'auto'
            ? 'auto-paper'
            : 'standard',
    label:
      format === 'cheetah'
        ? 'Cheetah LZ17 slips'
        : format === 'go4'
          ? 'GO4 (A4 slip + GoDEX outside)'
          : format === 'auto'
            ? 'Standard / Cheetah / GO4 by shipping method'
            : 'Standard A4 packing slips',
    detail:
      format === 'auto'
        ? 'Each order uses Standard, Cheetah, or GO4 from method / prior prints.'
        : format === 'go4'
          ? 'Tray 1 A4 slip then GoDEX outside label per order.'
          : 'One bundle per order, delivery-run order.',
  });
  if (recipe.includeInscriptions) {
    steps.push({
      key: 'inscription',
      label: 'A6 inscription cards',
      detail: 'Multipurpose tray. Face-up after the slip they belong to.',
    });
  }
  if (recipe.includeCarrierLabels) {
    steps.push({
      key: 'carrier',
      label: 'Carrier 4×6 labels',
      detail: 'GoDEX after the order paperwork, barcodes unrasterised.',
    });
  }
  if (recipe.includeSeparators !== false && (wave?.shippingMethods?.length || 0) > 1) {
    steps.push({
      key: 'separator',
      label: 'Start-of segment sheets',
      detail:
        'After each shipping-method label set: Start of cover with pick list, methods, and order count (lands on top face-up).',
    });
  }
  return steps;
}
